import { useTranslation } from 'core/i18n/i18n';
import { settingsModulePaths } from 'core/routes/routes';
import { useCallback } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { useNavigate } from 'react-router-dom';
import { MaterialDefinition } from '../material-definition.vm';
import { DeleteMaterialConfirmationComponent } from './components/delete-material-confirmation';
import { MaterialDefinitionsRowActionsComponent } from './components/material-definitions-row-actions';
import { useDefaultConfigMaterialDefinitionsList } from './hooks/use-default-config-material-definitions-list.hook';
import { useMaterialDefinitionDeleter } from './hooks/use-material-definition-deleter.hook';
import { useMaterialDefinitionsListSelection } from 'modules/settings/contexts/material-definitions/use-material-definitions-list-selection.hook';
import { SettingsMaterialDefinitionsListComponent } from './settings-material-definitions-list.component';

interface SettingsMaterialDefinitionsListContainerProps {
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

export const SettingsMaterialDefinitionsListContainer: React.FC<SettingsMaterialDefinitionsListContainerProps> = ({
    scrollContainerRef,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const popupManager = usePopupManager();
    const { getDefaultConfig } = useDefaultConfigMaterialDefinitionsList();
    const { deleteMaterial } = useMaterialDefinitionDeleter();
    const { materials, isLoading, defaultSort, onSortOrderChange, selectedRowIds, setSelectedRowIds, handleSelectRow } =
        useMaterialDefinitionsListSelection();

    const handleEdit = useCallback(
        (materialId: string) => {
            navigate(settingsModulePaths.materialDefinitionsForm.replace(':materialDefinitionId', materialId));
        },
        [navigate]
    );

    const handleDelete = useCallback(
        (materialId: string, materialName: string) => {
            popupManager.open(DeleteMaterialConfirmationComponent, {
                materialName,
                onConfirm: () => deleteMaterial(materialId),
            });
        },
        [popupManager, deleteMaterial]
    );

    const renderRowOptions = useCallback(
        (material: MaterialDefinition) => [
            <MaterialDefinitionsRowActionsComponent
                key={`material-definitions-row-actions-${material.id}`}
                materialId={material.id}
                materialName={material.name}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />,
        ],
        [handleEdit, handleDelete]
    );

    return (
        <SettingsMaterialDefinitionsListComponent
            title={t('settings.materials.material-definitions.title')}
            emptyMessage={t('settings.materials.material-definitions.no-materials-available')}
            materials={materials}
            isLoading={isLoading}
            config={getDefaultConfig()}
            scrollContainerRef={scrollContainerRef}
            defaultSort={defaultSort}
            selectedRowIds={selectedRowIds}
            setSelectedRowIds={setSelectedRowIds}
            onSortOrderChange={onSortOrderChange}
            onSelectRow={handleSelectRow}
            onRowClick={handleEdit}
            rowOptions={renderRowOptions}
        />
    );
};
